







































































































































































import { computed, defineComponent, onMounted, onUnmounted, ref, toRef, watch } from "@vue/composition-api";
import { Canvas } from "fabric/fabric-impl";
import { ContentPriceSubjectTypeEnum } from "@scrile/api-provider/dist/api/ContentPriceProvider";
import { ContentSet, InputContentSetUpdate } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { InputFileId } from "@scrile/api-provider/dist/api/FileProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import useFiles from "@/hooks/useFiles";
import useVaultView from "@/hooks/useVaultView";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { isAttachmentFile } from "@/lib/fileHelpers";
import { $t } from "@/lib/i18nHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { formatMoneyString } from "@/lib/moneyHelpers";
import { isGetUserMediaSupported } from "@/lib/webrtcHelpers";
import { AttachmentFile, PostFileData } from "@/types";
import { EVENTS } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseIcon from "@/components/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import BaseInputFile from "@/components/BaseInputFile";
import BaseSelect from "@/components/BaseSelect";
import BaseSpinner from "@/components/BaseSpinner";
import SaveDraftConfirm from "@/components/CreatePost/components/SaveDraftConfirm";
import { AddingFilesOptions } from "@/components/CreatePost/types";
import MobileSwitchCam from "@/components/MobileSwitchCam";
import ModalView from "@/components/ModalView";
import PageTitle from "@/components/PageTitle";
import VaultModal from "@/components/VaultModal";
import CreateStoryViewController from "./components/CreateStoryViewController";
import RecordStoryButton from "./components/RecordStoryButton";
import { GroupInstance, Overlay } from "./types";

const LOOP_DEFAULT_TIME = 30;

export default defineComponent({
  name: "CreateStoryView",
  components: {
    SaveDraftConfirm,
    BaseIconButton,
    CreateStoryViewController,
    RecordStoryButton,
    ModalView,
    PageTitle,
    BaseInputFile,
    BaseButton,
    BaseIcon,
    BaseSpinner,
    VaultModal,
    BaseSelect,
    MobileSwitchCam,
  },
  props: {
    fileList: { type: Array as () => PostFileData[], required: true },
    postData: { type: Object as () => ContentSet, required: true },
  },
  setup(props, { emit }) {
    const { uploadThenUpdate } = useFiles();
    const player = ref<HTMLVideoElement>();
    const cameraPlayer = ref<HTMLVideoElement>();
    const canvasRef = ref<HTMLCanvasElement>();
    const overlayRef = ref<HTMLCanvasElement>();
    const contentWrapperRef = ref<HTMLDivElement>();
    const storyRecordType = ref<"photo" | "video">("photo");
    const url = ref("");
    const mediaStream = ref<MediaStream>();
    /* eslint-disable @typescript-eslint/ban-ts-ignore */
    // @ts-ignore
    const recorder = ref<MediaRecorder | null>(null);
    const { showPrivateVault: showVault } = useVaultView();
    const timeoutId = ref<NodeJS.Timeout | number>(0);
    const timeoutResolve = ref<Function>(() => {});
    const recorderState = ref<"inactive" | "recording" | "paused">("inactive");
    const showPriceSettings = ref(false);
    const isEditOverlayMode = ref(false);
    const needRerenderOverlayController = ref(true);
    const storyOverlayFile = ref<File>();
    const storyOverlaySrc = ref<string>();
    const overlayInstance = ref<Canvas>();
    const overlayConfig = ref();
    const finishGroups = ref<GroupInstance[]>();
    const showDraftConfirm = ref(false);
    const overlay = computed<Overlay>(() => ({
      instance: overlayInstance.value,
      config: overlayConfig.value,
      groups: finishGroups.value,
      width: contentWrapperRef.value?.clientWidth || 0,
      height: contentWrapperRef.value?.clientHeight || 0,
    }));
    const isRecording = computed(() => recorderState.value === "recording");
    const addedFile = computed<PostFileData>(() => props.fileList[0]);
    const localVideoTracks = computed<MediaStreamTrack[]>(() => mediaStream.value?.getVideoTracks() ?? []);
    const mirrorVideo = computed<boolean>(() => {
      const settings = localVideoTracks.value.length > 0 && localVideoTracks.value[0].getSettings();
      return !settings || !settings.facingMode || settings.facingMode === "user";
    });

    const stop = () => {
      if (recorder.value && recorder.value.state !== "inactive") {
        recorder.value.stop();
      }
      mediaStream.value?.getTracks().forEach(track => track.stop());
    };

    const createVideoBlob = (chunks: Blob[]) => {
      const recordedBlob = new Blob(chunks, { type: "video/mp4" });
      url.value = URL.createObjectURL(recordedBlob);
      const file = new File([recordedBlob], `story_${new Date().toISOString()}.mp4`);
      emit("addFiles", [file], { publicAccess: true, unshift: true });
    };

    const start = async () => {
      if (!mediaStream.value) {
        return;
      }
      if (storyRecordType.value === "photo") {
        if (!canvasRef.value) {
          return;
        }
        if (!cameraPlayer.value) {
          return;
        }
        canvasRef.value.width = cameraPlayer.value.videoWidth;
        canvasRef.value.height = cameraPlayer.value.videoHeight;
        const canvasCtx = canvasRef.value.getContext("2d");
        if (!canvasCtx) {
          return;
        }
        canvasCtx.drawImage(cameraPlayer.value, 0, 0, cameraPlayer.value.videoWidth, cameraPlayer.value.videoHeight);
        canvasRef.value.toBlob((blob: Blob | null) => {
          if (blob) {
            const file = new File([blob], `photo${new Date().toISOString()}.jpg`, { type: "image/jpeg" });
            stop();
            emit("addFiles", [file], { publicAccess: true, unshift: true });
          }
        }, "image/jpeg");
      } else {
        const data: Blob[] = [];
        // @ts-ignore
        recorder.value = new MediaRecorder(mediaStream.value);
        // @ts-ignore
        recorder.value.ondataavailable = (event: BlobEvent) => data.push(event.data);
        recorder.value.start();
        recorderState.value = "recording";

        const triggerTimeout = () => {
          typeof timeoutId.value === "number" ? clearTimeout(timeoutId.value) : clearTimeout(timeoutId.value);
          timeoutResolve.value();
          timeoutId.value = 0;
          timeoutResolve.value = () => {};
        };

        const stopped = new Promise<void>((resolve, reject) => {
          (recorder.value as MediaRecorder).onstop = () => {
            recorderState.value = "inactive";
            triggerTimeout();
            resolve();
          };
          (recorder.value as MediaRecorder).onerror = event => {
            recorderState.value = "inactive";
            triggerTimeout();
            reject(event.error.name);
          };
        });
        const recorded = new Promise(r => {
          timeoutId.value = setTimeout(r, LOOP_DEFAULT_TIME * 1000);
          timeoutResolve.value = r;
        }).then(() => {
          isRecording.value && stop();
        });

        Promise.all([stopped, recorded]).then(() => createVideoBlob(data));
      }
    };

    const startUserMedia = async () => {
      if (isGetUserMediaSupported() && !mediaStream.value?.active) {
        const stream: MediaStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (addedFile.value) {
          stop();
        } else {
          mediaStream.value = stream;
          if (cameraPlayer.value) {
            cameraPlayer.value.srcObject = stream;
          }
        }
      }
    };

    const handleShowPrice = () => {
      showPriceSettings.value = true;
      needRerenderOverlayController.value = true;
    };
    const handleCameraSwitch = (trackData: MediaStreamConstraints) => {
      stop();
      navigator.mediaDevices.getUserMedia({ ...trackData, audio: true }).then((stream: MediaStream) => {
        mediaStream.value = stream;
        if (cameraPlayer.value) {
          cameraPlayer.value.srcObject = stream;
        }
      });
    };
    const handleTypeChange = (newType: "photo" | "video") => (storyRecordType.value = newType);
    const handleCameraStart = () => {
      if (!isRecording.value && mediaStream.value) {
        start();
      } else if (mediaStream.value) {
        stop();
      }
    };
    const handleCancel = () => {
      isEditOverlayMode.value = false;
      emit("cancel");
    };
    const handleSaveOverlay = async (url: string, groups: GroupInstance[], config?: any, instance?: Canvas) => {
      if (url !== "") {
        const response = await fetch(url);
        const data = await response.blob();
        const metadata = { type: "image/png" };

        storyOverlayFile.value = new File([data], `story_overlay.png`, metadata);
      }

      storyOverlaySrc.value = url;
      overlayInstance.value = instance;
      overlayConfig.value = config;
      finishGroups.value = groups;

      isEditOverlayMode.value = false;
      needRerenderOverlayController.value = false;
    };

    const resizeHandler = () => {
      overlay.value.width = contentWrapperRef.value?.clientWidth ?? 0;
      overlay.value.height = contentWrapperRef.value?.clientHeight ?? 0;
    };

    watch(
      toRef(props, "fileList"),
      newVal => {
        if (newVal) {
          stop();
        }
      },
      { immediate: true }
    );

    const closeModal = () => {
      if (!props.fileList.length) {
        emit("cancel");
        return;
      }
      showDraftConfirm.value = true;
    };

    onMounted(() => {
      startUserMedia();
      window.addEventListener("resize", resizeHandler);
    });
    onUnmounted(() => {
      stop();
      window.removeEventListener("resize", resizeHandler);
    });

    return {
      mirrorVideo,
      localVideoTracks,
      storyRecordType,
      url,
      storyOverlayFile,
      storyOverlaySrc,
      addedFile,
      isEditOverlayMode,
      isRecording,
      player,
      cameraPlayer,
      canvasRef,
      contentWrapperRef,
      overlayRef,
      overlay,
      showVault,
      showPriceSettings,
      showDraftConfirm,
      needRerenderOverlayController,
      startUserMedia,
      handleShowPrice,
      handleCameraSwitch,
      handleTypeChange,
      handleCameraStart,
      handleSaveOverlay,
      handleCancel,
      uploadThenUpdate,
      closeModal,
    };
  },
  data() {
    return {
      processing: false,
      publishing: false,
      isConverting: false,
      showUploadingProgress: false,
      isMuted: true,
      isPaused: true,
      priceList: [] as SubscriptionLevel[],
      levelId: "0",
    };
  },
  computed: {
    poster(): string {
      return !!this.addedFile && this.addedFile.url.length > 0
        ? MediaFilesController.getVideoThumbnail(this.addedFile.url, "1080p")
        : "";
    },
    isImage(): boolean {
      return !!this.addedFile && this.addedFile.mediaType.includes("image");
    },
    publicAccess(): boolean {
      return this.levelId === "0";
    },
    selectOptions(): { text: string; value: string }[] {
      return [
        {
          text: $t("Everyone"),
          value: "0",
        },
      ].concat(
        this.priceList.map((price, index) => {
          const prices = formatMoneyString(price.pricePackage?.amount ?? 0);
          return {
            text:
              index === this.priceList.length - 1
                ? $t("{0} subscribers", [prices])
                : $t("{0} subscribers and higher", [prices]),
            value: price.id,
          };
        })
      );
    },
  },
  watch: {
    addedFile: {
      handler(newVal: PostFileData | undefined, oldVal: PostFileData | undefined) {
        if (newVal !== undefined && oldVal === undefined) {
          if (this.addedFile.file) {
            this.url = URL.createObjectURL(newVal.file);
          } else {
            this.getDraftedFile(this.fileList[0]);
          }
        }
        if (newVal == undefined && oldVal !== undefined) {
          this.url = "";
          this.isMuted = true;
          this.isPaused = true;
          this.startUserMedia();
        }
        if (newVal?.uploading && (oldVal === undefined || !oldVal.uploading)) {
          this.$nextTick().then(() => {
            if (!this.isImage) {
              this.player
                ?.play()
                .catch(() => {
                  this.showUploadingProgress = true;
                  this.player?.play().then(() => (this.showUploadingProgress = false));
                })
                .then(() => {
                  this.isPaused = false;
                });
            }
          });
        }
        if (oldVal?.processingStatus === "NEW" && newVal?.processingStatus === "OK") {
          this.showUploadingProgress = false;
          if (!this.isImage) {
            this.player?.play().catch(() => {
              this.isPaused = true;
              this.checkStatus(newVal);
            });
          }
        }
      },
    },
  },
  methods: {
    async uploadOverlay() {
      if (!this.storyOverlayFile) return;
      const fileId: InputFileId = {
        type: "story_overlay",
        subjectId: this.postData.id,
      };

      await this.uploadThenUpdate({ fileId, file: this.storyOverlayFile });
    },
    onPublish() {
      this.publishing = true;
      const data: Omit<InputContentSetUpdate, "id"> = {
        published: true,
      };
      this.$emit(
        "update",
        {
          data,
          closeOnSave: true,
        },
        async () => {
          this.publishing = false;
          await this.levelUpdate();
          await this.uploadOverlay();
        }
      );
    },
    async checkStatus(fileObj: PostFileData) {
      let timerId: ReturnType<typeof setTimeout> | undefined;
      const result = await fetch(MediaFilesController.getVideoUrl(fileObj.url), {
        method: "HEAD",
      });
      const headerProcessingStatus = result.headers.get("x-amz-meta-conversion-status");
      if (headerProcessingStatus === "OK") {
        this.isConverting = false;
        this.url = MediaFilesController.getVideoUrl(fileObj.url);
        this.isMuted = true;
        this.isPaused = false;
        timerId && clearTimeout(timerId);
      } else {
        this.isConverting = true;
        timerId = setTimeout(() => {
          this.checkStatus(fileObj);
        }, 5000);
      }
    },
    onAddFiles(files: (AttachmentFile | File)[], options: AddingFilesOptions) {
      if (files.every(isAttachmentFile)) {
        this.$emit("addFilesFromVault", files, options);
      } else {
        this.$emit("addFiles", files, options);
      }
      this.isMuted = true;
      this.isPaused = true;
    },
    getDraftedFile(fileObj: PostFileData) {
      this.url = `${MediaFilesController.getVideoUrl(fileObj.url)}?=`;
      if (!this.isImage && this.player?.paused) {
        this.checkStatus(fileObj);
      } else {
        this.isPaused = false;
      }
    },
    playingLoop() {
      if (this.player) {
        const currentTime = Math.floor(this.player.currentTime);
        if (currentTime >= LOOP_DEFAULT_TIME) this.player.currentTime = 0;
      }
    },
    getLevelsData() {
      Promise.all([
        providers.ContentSubscriptionLevelProvider.search({
          subjects: [
            {
              subjectType: ContentPriceSubjectTypeEnum.CONTENT_SET,
              subjectId: this.postData.id,
            },
          ],
        }).then(r => (r.find(i => i.subject.subjectId === this.postData.id) || { levelId: "0" }).levelId),
        providers.SubscriptionLevelProvider.find({ userIds: [this.postData.userId] }).then(r => (this.priceList = r)),
      ]).then(([levelId]) => {
        if (this.levelId === levelId) return;
        const priceChanged = this.levelId !== levelId;
        this.levelId = levelId;
        if (priceChanged) {
          emitter.emit(EVENTS.CONTENT_FEED_PRICE_LOADED, {
            recurringLevel: this.priceList.find(i => i.id === this.levelId) || null,
            oneTimePackage: null,
          });
        }
      });
    },
    async changeSelect(value: string) {
      this.levelId = value;
      emitter.emit(EVENTS.CONTENT_FEED_PRICE_CHANGED, {
        recurringLevel: this.priceList.find(i => i.id === this.levelId) || null,
        oneTimePackage: null,
      });
    },
    async levelUpdate() {
      await providers.ContentSubscriptionLevelProvider.update({
        data: {
          levelId: this.levelId,
          subject: {
            subjectType: ContentPriceSubjectTypeEnum.CONTENT_SET,
            subjectId: this.postData.id,
          },
        },
      });
    },
  },
  async created() {
    this.processing = true;
    try {
      await this.getLevelsData();
    } finally {
      this.processing = false;
    }
    this.$emit("onSetUpdateCb", () => this.levelUpdate());
  },
  mounted() {
    if (this.fileList.length) {
      this.getDraftedFile(this.fileList[0]);
    }
  },
});
